// components/settings/AcknowledgementsPanel.tsx
// -----------------------------------------------------------------------------
// Side panel listing open-source packages used by the web app.
// Mirrors the mobile acknowledgements screen layout.
// -----------------------------------------------------------------------------

import { useMemo } from "react";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
} from "@/components/ui/SidePanel";
import { Icon } from "@/components/ui/Icon";
import licensesData from "@/assets/licenses.json";

interface LicenseEntry {
  name: string;
  version: string;
  type?: string;
  url: string;
  description?: string;
  author?: string;
}

/** Extract GitHub owner from repo URL to build an avatar URL. */
function getGitHubAvatarUrl(repoUrl: string): string | null {
  try {
    const url = new URL(repoUrl);
    if (url.hostname === "github.com") {
      const pathParts = url.pathname.split("/").filter(Boolean);
      if (pathParts.length >= 1) {
        return `https://github.com/${pathParts[0]}.png?size=64`;
      }
    }
  } catch {
    // Invalid URL
  }
  return null;
}

interface AcknowledgementsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AcknowledgementsPanel({
  open,
  onOpenChange,
}: AcknowledgementsPanelProps) {
  const licenses = useMemo(() => {
    const entries = Object.entries(
      licensesData as Record<string, LicenseEntry>
    ).map(([key, value]) => ({ key, ...value }));
    return entries.sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelHeader title="Acknowledgements" />
        <SidePanelBody>
          <p className="text-body-2 text-text-secondary mb-6">
            TinyClub is built with the help of these amazing open source
            projects.
          </p>

          <div className="space-y-0">
            {licenses.map((item, index) => {
              const avatarUrl = getGitHubAvatarUrl(item.url);
              return (
                <div key={item.key}>
                  {index > 0 && (
                    <div className="h-px bg-border-subtle ml-11" />
                  )}
                  <button
                    className="flex items-center gap-3 py-4 w-full hover:opacity-80 transition-opacity cursor-pointer text-left"
                    onClick={() => window.open(item.url, "_blank")}
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt=""
                        className="w-8 h-8 rounded-lg bg-surface-2 object-cover flex-shrink-0"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-surface-2 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-body-1 text-text-primary truncate">
                        {item.name}
                      </p>
                      <p className="text-body-3 text-text-secondary mt-0.5">
                        {item.version}
                        {item.type ? ` \u00B7 ${item.type}` : ""}
                      </p>
                    </div>
                    <Icon
                      name="angle-right"
                      size={16}
                      className="text-text-secondary flex-shrink-0"
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </SidePanelBody>
      </SidePanelContent>
    </SidePanel>
  );
}
